<template>
	<div
		v-if="showWidget"
		class="wsp"
	>
		<!-- ===== Alça: substitui a aba quando ela cobriria algum componente da tela ===== -->
		<button
			v-if="collapsed && tabHidden"
			class="wsp-handle"
			type="button"
			:title="$t('walteriaOpenTab')"
			:aria-label="$t('walteriaOpenTab')"
			@click="openPanel"
		>
			<WalterIAIcon
				class="wsp-handle__icon"
				:size="16"
				color="#FEFEFE"
			/>
		</button>

		<!-- ===== Aba recolhida (borda esquerda) ===== -->
		<button
			v-else-if="collapsed"
			ref="tab"
			class="wsp-tab wsp-tab--collapsed"
			type="button"
			@click="openPanel"
		>
			<i class="fas fa-angle-double-right wsp-tab__chevron" />
			<div class="wsp-tab__rot">
				<div class="wsp-tab__group">
					<WalterIAIcon
						:size="22"
						color="#95BB49"
					/>
					<span class="wsp-tab__name">Walter <span class="wsp-tab__ia">IA</span></span>
				</div>
			</div>
		</button>

		<!-- ===== Painel aberto ===== -->
		<div
			v-else
			class="wsp-panel"
			@click="dropdownOpen = false"
		>
			<!-- header -->
			<div class="wsp-header">
				<WalterIAIcon
					:size="36"
					color="#95BB49"
				/>
				<span class="wsp-logo">Walter <span class="wsp-logo__ia">IA</span></span>
			</div>

			<!-- seletor de conversa + nova -->
			<div class="wsp-top">
				<div class="wsp-dropdown">
					<button
						class="wsp-dropdown__field"
						type="button"
						@click.stop="dropdownOpen = !dropdownOpen"
					>
						<span class="wsp-dropdown__label">{{ currentTitle }}</span>
						<i class="fas fa-chevron-down" />
					</button>
					<div
						v-if="dropdownOpen"
						class="wsp-dropdown__menu"
						@click.stop
					>
						<div
							class="wsp-dropdown__item"
							@click="selectNew"
						>
							<i class="fas fa-plus" /> {{ $t('walteriaNewConversation') }}
						</div>
						<div
							v-if="sessions.length === 0"
							class="wsp-dropdown__empty"
						>
							{{ $t('walteriaNoConversations') }}
						</div>
						<div
							v-for="s in sessions"
							:key="s.id"
							class="wsp-dropdown__item"
							:class="{ 'is-active': s.id === currentSessionId }"
							@click="selectSession(s.id)"
						>
							<i class="fas fa-comment" /> {{ s.title || $t('walteriaNewConversation') }}
						</div>
					</div>
				</div>
				<button
					class="wsp-newbtn"
					type="button"
					:title="$t('walteriaNewConversation')"
					@click="newConversation"
				>
					<i class="fas fa-plus" />
				</button>
			</div>

			<!-- mensagens -->
			<div
				ref="msgs"
				class="wsp-messages"
			>
				<div
					v-if="loadingMessages"
					class="wsp-center"
				>
					<div class="wsp-dots">
						<span style="animation-delay: 0ms" />
						<span style="animation-delay: 150ms" />
						<span style="animation-delay: 300ms" />
					</div>
				</div>

				<template v-else>
					<div
						v-if="messages.length === 0 && !streaming"
						class="wsp-welcome"
					>
						<WalterIAIcon
							:size="64"
							color="#95BB49"
						/>
						<p class="wsp-welcome__text">{{ $t('walteriaAskSomething') }}</p>
					</div>

					<div
						v-for="(msg, i) in messages"
						:key="i"
						class="wsp-msg"
					>
						<div
							v-if="msg.role === 'user'"
							class="wsp-row wsp-row--user"
						>
							<div class="wsp-bubble wsp-bubble--user">{{ msg.content }}</div>
							<img
								class="wsp-avatar wsp-avatar--user"
								:src="userPhotoUrl"
								alt=""
								@error="onAvatarError"
							/>
						</div>
						<div
							v-else
							class="wsp-row wsp-row--bot"
						>
							<div class="wsp-avatar wsp-avatar--bot">
								<WalterIAIcon
									:size="16"
									color="#95BB49"
								/>
							</div>
							<div class="wsp-bubble wsp-bubble--bot">
								<MessageContent
									:content="msg.content"
									:export-context="exportContextFor(i)"
								/>
							</div>
						</div>
					</div>

					<div
						v-if="streaming"
						class="wsp-row wsp-row--bot"
					>
						<div class="wsp-avatar wsp-avatar--bot">
							<WalterIAIcon
								:size="16"
								color="#95BB49"
							/>
						</div>
						<div class="wsp-bubble wsp-bubble--bot">
							<MessageContent
								v-if="streamingContent"
								:content="streamingContent"
								:export-context="exportContextFor(messages.length)"
							/>
							<!-- Fica visível até o fim do processamento, inclusive nas pausas em que o
							     Walter já escreveu algo e está consultando dados. -->
							<div
								class="wsp-typing"
								:class="{ 'wsp-typing--inline': streamingContent }"
								role="status"
								aria-live="polite"
							>
								<div class="wsp-dots wsp-dots--sm">
									<span style="animation-delay: 0ms" />
									<span style="animation-delay: 150ms" />
									<span style="animation-delay: 300ms" />
								</div>
								<span
									v-if="!streamingContent"
									class="wsp-typing__label"
								>
									{{ $t('walteriaThinking') }}
								</span>
							</div>
						</div>
					</div>
				</template>
			</div>

			<!-- composer -->
			<div class="wsp-composer">
				<div class="wsp-field">
					<textarea
						ref="input"
						v-model="inputText"
						class="wsp-field__input"
						:placeholder="$t('walteriaAskWalter')"
						rows="1"
						@keydown.enter.exact.prevent="sendMessage"
						@input="autoResize"
					/>
					<button
						class="wsp-send"
						:class="{ 'is-disabled': !inputText.trim() || streaming }"
						:disabled="!inputText.trim() || streaming"
						type="button"
						@mousedown.prevent
						@click="sendMessage"
					>
						<i class="fas fa-paper-plane" />
					</button>
				</div>
				<p class="wsp-hint">{{ $t('walteriaDisclaimer') }}</p>
			</div>

			<!-- aba recolher (borda direita do painel) -->
			<button
				class="wsp-tab wsp-tab--open"
				type="button"
				@click="closePanel"
			>
				<i class="fas fa-angle-double-left wsp-tab__chevron" />
				<div class="wsp-tab__rot">
					<div class="wsp-tab__group">
						<WalterIAIcon
							:size="22"
							color="#95BB49"
						/>
						<span class="wsp-tab__name">Walter <span class="wsp-tab__ia">IA</span></span>
					</div>
				</div>
			</button>
		</div>
	</div>
</template>

<script>
import WalterIARequests from '../../services/requests/WalterIARequests'
import MessageContent from '../../views/WalterIA/components/MessageContent.vue'
import WalterIAIcon from './WalterIAIcon.vue'
import { ChatStreamProvider } from '../../@web/modules/walteria/providers/ChatStream.provider'
import { TabOverlapProvider } from '../../@web/modules/walteria/providers/TabOverlap.provider'

// Posição da aba recolhida (espelha o CSS de .wsp-tab--collapsed) e tamanho usado até a
// primeira medição real. Com a aba oculta, a checagem continua sobre essa área "virtual",
// senão a aba nunca saberia a hora de voltar.
const TAB_TOP_RATIO = 0.45
const TAB_DEFAULT_SIZE = { width: 38, height: 212 }
// Agrupa rajadas de mudanças no DOM (render de tabela, animações) numa checagem só.
const OVERLAP_CHECK_DELAY_MS = 150

export default {
	name: 'WalterIASidePanel',
	components: { MessageContent, WalterIAIcon },
	data() {
		return {
			collapsed: true,
			tabHidden: false,
			dropdownOpen: false,
			sessions: [],
			currentSessionId: null,
			messages: [],
			inputText: '',
			streaming: false,
			streamingContent: '',
			loadingMessages: false,
			avatarFailed: false,
		}
	},
	computed: {
		// esconde o widget na própria rota do WalterIA (evita duplicidade)
		showWidget() {
			return this.$route && this.$route.name !== 'walteria'
		},
		currentTitle() {
			if (!this.currentSessionId) return this.$t('walteriaNewConversation')
			const s = this.sessions.find((x) => x.id === this.currentSessionId)
			return (s && s.title) || this.$t('walteriaNewConversation')
		},
		userPhotoUrl() {
			const foto = localStorage.getItem('fotoUrl')
			if (foto && !this.avatarFailed) return foto
			const username = (localStorage.getItem('username') || 'U').split('@')[0]
			return `https://ui-avatars.com/api/?name=${username}&size=32`
		},
	},
	watch: {
		$route() {
			this.scheduleOverlapCheck()
		},
		collapsed(value) {
			if (value) this.scheduleOverlapCheck()
		},
	},
	mounted() {
		this.tabSize = { ...TAB_DEFAULT_SIZE }
		this.overlapTimer = null
		// Pega tudo que pode colocar algo embaixo da aba: telas renderizando, menus abrindo,
		// rolagem (inclusive de containers internos, por isso o capture) e redimensionamento.
		this.overlapObserver = new MutationObserver(this.scheduleOverlapCheck)
		this.overlapObserver.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['class', 'style'],
		})
		window.addEventListener('resize', this.scheduleOverlapCheck)
		window.addEventListener('scroll', this.scheduleOverlapCheck, true)
		this.scheduleOverlapCheck()
	},
	beforeDestroy() {
		if (this.overlapObserver) this.overlapObserver.disconnect()
		window.removeEventListener('resize', this.scheduleOverlapCheck)
		window.removeEventListener('scroll', this.scheduleOverlapCheck, true)
		clearTimeout(this.overlapTimer)
	},
	updated() {
		this.scrollToBottom()
	},
	methods: {
		openPanel() {
			this.collapsed = false
			if (this.sessions.length === 0) this.loadSessions()
			this.$nextTick(() => this.$refs.input && this.$refs.input.focus())
		},
		closePanel() {
			this.collapsed = true
			this.dropdownOpen = false
		},
		scheduleOverlapCheck() {
			if (this.overlapTimer) return
			this.overlapTimer = setTimeout(() => {
				this.overlapTimer = null
				this.checkOverlap()
			}, OVERLAP_CHECK_DELAY_MS)
		},
		// Oculta a aba (deixando só a alça) enquanto ela cobrir algum componente, e a traz de
		// volta quando o espaço fica livre. Com o painel aberto não há aba para checar.
		checkOverlap() {
			if (!this.collapsed || !this.$el || !this.$el.contains) return
			if (this.$refs.tab) {
				const r = this.$refs.tab.getBoundingClientRect()
				if (r.width && r.height) this.tabSize = { width: r.width, height: r.height }
			}
			const hidden = TabOverlapProvider.isOverlapping({
				rect: {
					left: 0,
					top: window.innerHeight * TAB_TOP_RATIO,
					width: this.tabSize.width,
					height: this.tabSize.height,
				},
				elementsAt: (x, y) => document.elementsFromPoint(x, y),
				viewportWidth: document.documentElement.clientWidth,
				isOwn: (el) => this.$el.contains(el),
			})
			if (hidden !== this.tabHidden) this.tabHidden = hidden
		},
		async loadSessions() {
			try {
				const res = await WalterIARequests.getSessions()
				this.sessions = res.data || []
			} catch {
				console.error('Erro ao carregar sessões')
			}
		},
		async loadSession(sessionId) {
			this.currentSessionId = sessionId
			this.loadingMessages = true
			this.dropdownOpen = false
			try {
				const res = await WalterIARequests.getMessages(sessionId)
				this.messages = res.data || []
			} catch {
				console.error('Erro ao carregar mensagens')
			} finally {
				this.loadingMessages = false
			}
		},
		selectSession(id) {
			this.loadSession(id)
		},
		selectNew() {
			this.dropdownOpen = false
			this.newConversation()
		},
		newConversation() {
			this.currentSessionId = null
			this.messages = []
			this.inputText = ''
			this.dropdownOpen = false
			this.$nextTick(() => this.$refs.input && this.$refs.input.focus())
		},
		async sendMessage() {
			const text = this.inputText.trim()
			if (!text || this.streaming) return
			this.messages.push({ role: 'user', content: text })
			// O campo segue habilitado durante a resposta e o botão não rouba o foco (mousedown.prevent),
			// então o cursor fica no campo; o focus cobre o envio pelo teclado (Tab até o botão).
			this.$nextTick(() => this.$refs.input && this.$refs.input.focus())
			this.inputText = ''
			const payload = { message: text, sessionId: this.currentSessionId }
			this.streaming = true
			this.streamingContent = ''
			try {
				const fim = await ChatStreamProvider.run(
					(signal) => WalterIARequests.streamChat(payload, signal),
					{
						onSession: (sessionId) => {
							if (!this.currentSessionId) this.currentSessionId = sessionId
						},
						onDelta: (content) => {
							this.streamingContent += content
						},
					},
				)
				this.messages.push({ role: 'assistant', content: this.respostaFinal(fim) })
			} catch {
				try {
					const res = await WalterIARequests.sendChat(payload)
					// A API responde ChatResponse(Response, SessionId), ou seja o campo e 'response'.
					// Antes lia 'message'/'content', que nunca existem: o fallback exibia erro mesmo
					// com HTTP 200 e a resposta do assistente era perdida.
					const msg = res.data?.response || this.$t('walteriaErrorProcessing')
					this.messages.push({ role: 'assistant', content: msg })
					if (res.data?.sessionId) this.currentSessionId = res.data.sessionId
				} catch {
					this.messages.push({
						role: 'assistant',
						content: this.$t('walteriaErrorSending'),
					})
				}
			} finally {
				this.streaming = false
				this.streamingContent = ''
				await this.loadSessions()
				this.$nextTick(() => this.autoResize())
			}
		},
		// Texto que fica no histórico ao fim do stream. Em erro, mantém o que já chegou e acrescenta
		// o aviso: antes o evento "error" do backend era ignorado e sobrava um balão vazio.
		respostaFinal(fim) {
			const avisos = {
				error: fim.message || this.$t('walteriaErrorProcessing'),
				interrupted: this.$t('walteriaErrorInterrupted'),
				timeout: this.$t('walteriaErrorTimeout'),
			}
			const aviso = avisos[fim.kind]
			if (!aviso) return this.streamingContent
			return this.streamingContent ? `${this.streamingContent}\n\n⚠️ ${aviso}` : `⚠️ ${aviso}`
		},
		onAvatarError() {
			this.avatarFailed = true
		},
		// Contexto para nomear arquivos exportados da resposta na posição `index`: a pergunta do
		// usuário que a originou ou, sem ela, o título da conversa.
		exportContextFor(index) {
			for (let k = index - 1; k >= 0; k--) {
				if (this.messages[k].role === 'user') return this.messages[k].content
			}
			const session = this.sessions.find((s) => s.id === this.currentSessionId)
			return (session && session.title) || ''
		},
		autoResize() {
			const el = this.$refs.input
			if (!el) return
			el.style.height = 'auto'
			el.style.height = Math.min(el.scrollHeight, 120) + 'px'
		},
		scrollToBottom() {
			this.$nextTick(() => {
				const el = this.$refs.msgs
				if (el) el.scrollTop = el.scrollHeight
			})
		},
	},
}
</script>

<style scoped>
.wsp {
	font-family: 'Open Sans', sans-serif;
}

/* ===== Aba (recolhida / recolher) ===== */
.wsp-tab {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
	padding: 20px 6px;
	background: #fefefe;
	border: 1px solid #737576;
	border-left: none;
	border-radius: 0 8px 8px 0;
	cursor: pointer;
	box-shadow: 2px 2px 8px rgba(1, 57, 94, 0.12);
}
.wsp-tab--collapsed {
	position: fixed;
	left: 0;
	top: 45%;
	z-index: 1000;
}

/* alça (aba oculta): fina para não cobrir o conteúdo; ao passar o mouse alarga e mostra a
   flor do Walter, e o clique abre o painel direto */
.wsp-handle {
	position: fixed;
	left: 0;
	top: 45%;
	z-index: 1000;
	width: 6px;
	height: 64px;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	overflow: hidden;
	background: #95bb49;
	border: none;
	border-radius: 0 6px 6px 0;
	box-shadow: 2px 2px 6px rgba(1, 57, 94, 0.12);
	cursor: pointer;
	transition: width 0.15s ease;
}
.wsp-handle:hover,
.wsp-handle:focus-visible {
	width: 26px;
}
.wsp-handle__icon {
	flex-shrink: 0;
	opacity: 0;
	transition: opacity 0.15s ease;
}
.wsp-handle:hover .wsp-handle__icon,
.wsp-handle:focus-visible .wsp-handle__icon {
	opacity: 1;
}
.wsp-tab--open {
	position: absolute;
	left: 100%;
	top: 50%;
	transform: translateY(-50%);
	z-index: 1;
}
.wsp-tab__chevron {
	font-size: 16px;
	color: #01395e;
}
/* grupo flor + texto girado -90° (igual ao design) */
.wsp-tab__rot {
	width: 22px;
	height: 130px;
	display: flex;
	align-items: center;
	justify-content: center;
}
.wsp-tab__group {
	display: flex;
	align-items: center;
	gap: 10px;
	white-space: nowrap;
	transform: rotate(-90deg);
}
.wsp-tab__name {
	font-size: 18px;
	font-weight: 700;
	letter-spacing: -0.5px;
	color: #272728;
}
.wsp-tab__ia {
	color: #95bb49;
}

/* ===== Painel ===== */
.wsp-panel {
	position: fixed;
	left: 0;
	top: 0;
	height: 100vh;
	width: min(560px, 92vw);
	background: #fefefe;
	border-right: 1px solid #737576;
	border-top-right-radius: 40px;
	box-shadow: 6px 0 24px rgba(1, 57, 94, 0.14);
	z-index: 1000;
	display: flex;
	flex-direction: column;
	padding: 24px 24px 20px;
	box-sizing: border-box;
	gap: 16px;
}

.wsp-header {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	flex-shrink: 0;
}
.wsp-logo {
	font-size: 26px;
	font-weight: 700;
	letter-spacing: -1.5px;
	color: #272728;
}
.wsp-logo__ia {
	color: #95bb49;
}

/* seletor + nova */
.wsp-top {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-shrink: 0;
}
.wsp-dropdown {
	position: relative;
	flex: 1;
	min-width: 0;
}
.wsp-dropdown__field {
	width: 100%;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	padding: 0 8px 0 12px;
	background: #fefefe;
	border: 1px solid #737576;
	border-radius: 4px;
	cursor: pointer;
	font-family: inherit;
}
.wsp-dropdown__label {
	flex: 1;
	min-width: 0;
	text-align: left;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 15px;
	color: #272728;
}
.wsp-dropdown__field i {
	font-size: 14px;
	color: #737576;
}
.wsp-dropdown__menu {
	position: absolute;
	top: calc(100% + 4px);
	left: 0;
	right: 0;
	max-height: 260px;
	overflow-y: auto;
	background: #fefefe;
	border: 1px solid #ccd7df;
	border-radius: 8px;
	box-shadow: 0 12px 28px rgba(1, 57, 94, 0.16);
	z-index: 5;
	padding: 6px;
}
.wsp-dropdown__item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 10px;
	border-radius: 6px;
	font-size: 14px;
	color: #272728;
	cursor: pointer;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.wsp-dropdown__item i {
	font-size: 11px;
	color: #99b0bf;
	flex-shrink: 0;
}
.wsp-dropdown__item:hover {
	background: #f1f5f8;
}
.wsp-dropdown__item.is-active {
	background: #eaf1db;
}
.wsp-dropdown__empty {
	padding: 12px 10px;
	font-size: 12px;
	color: #9aa3a8;
	text-align: center;
}
.wsp-newbtn {
	flex-shrink: 0;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #95bb49;
	border: 1px solid #272728;
	border-radius: 4px;
	color: #272728;
	font-size: 16px;
	cursor: pointer;
}
.wsp-newbtn:hover {
	background: #88ac41;
}

/* mensagens */
.wsp-messages {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	background: #f1f5f8;
	border-radius: 8px;
	padding: 16px;
}
.wsp-center {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
}
.wsp-welcome {
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	text-align: center;
}
.wsp-welcome__text {
	margin: 0;
	font-size: 15px;
	color: #464947;
}
.wsp-msg {
	margin-bottom: 12px;
}
.wsp-row {
	display: flex;
	gap: 8px;
	align-items: flex-end;
}
.wsp-row--user {
	justify-content: flex-end;
}
.wsp-row--bot {
	justify-content: flex-start;
}
.wsp-avatar {
	width: 28px;
	height: 28px;
	min-width: 28px;
	border-radius: 50%;
	flex-shrink: 0;
	box-sizing: border-box;
}
.wsp-avatar--bot {
	display: flex;
	align-items: center;
	justify-content: center;
	background: #fefefe;
	border: 1px solid #272728;
}
.wsp-avatar--user {
	object-fit: cover;
	background: #95bb49;
	border: 1px solid #272728;
}
.wsp-bubble {
	max-width: 78%;
	padding: 10px 14px;
	font-size: 15px;
	line-height: 20px;
	word-break: break-word;
}
.wsp-bubble--user {
	background: #34617e;
	border: 1px solid #012238;
	color: #fefefe;
	text-align: right;
	white-space: pre-wrap;
	border-radius: 16px 16px 4px 16px;
}
.wsp-bubble--bot {
	background: #fefefe;
	border: 1px solid #272728;
	color: #272728;
	border-radius: 16px 16px 16px 4px;
}

/* composer */
.wsp-composer {
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
}
.wsp-field {
	display: flex;
	align-items: center;
	gap: 12px;
	min-height: 56px;
	padding: 8px 16px;
	background: #fefefe;
	border: 1px solid #737576;
	border-radius: 4px;
	box-sizing: border-box;
}
.wsp-field:focus-within {
	border-color: #95bb49;
}
.wsp-field__input {
	flex: 1;
	min-width: 0;
	border: none;
	outline: none;
	resize: none;
	background: transparent;
	color: #272728;
	font-size: 15px;
	line-height: 20px;
	font-family: inherit;
	max-height: 120px;
	overflow-y: auto;
}
.wsp-field__input::placeholder {
	color: #737576;
}
.wsp-send {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	background: #95bb49;
	border: 1px solid #272728;
	border-radius: 4px;
	color: #272728;
	font-size: 16px;
	cursor: pointer;
}
.wsp-send:hover {
	background: #88ac41;
}
.wsp-send.is-disabled {
	opacity: 0.45;
	cursor: not-allowed;
}
.wsp-hint {
	margin: 0;
	text-align: center;
	font-size: 13px;
	line-height: 16px;
	color: #272728;
}

/* dots */
.wsp-dots {
	display: flex;
	gap: 6px;
}
.wsp-dots span {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: #95bb49;
	display: inline-block;
	animation: wspBounce 1s infinite;
}
.wsp-dots--sm span {
	width: 6px;
	height: 6px;
}
/* Indicador de "digitando" enquanto o Walter processa a resposta */
.wsp-typing {
	display: flex;
	align-items: center;
	gap: 8px;
}
.wsp-typing--inline {
	margin-top: 6px;
}
.wsp-typing__label {
	font-size: 12px;
	color: #737576;
}
/* Antes subia só 25% do ponto (1,5px) e parecia parado; agora o movimento e o brilho
   escalonados deixam claro que há processamento em andamento. */
@keyframes wspBounce {
	0%,
	60%,
	100% {
		transform: translateY(0);
		opacity: 0.4;
	}
	30% {
		transform: translateY(-5px);
		opacity: 1;
	}
}
@media (prefers-reduced-motion: reduce) {
	.wsp-dots span {
		animation: none;
		opacity: 0.7;
	}
}
</style>
